import type { AppTourStep } from '@fordeer/app-tour'
import {
  INVOICE_TOUR_TARGET_KEY,
  resolveTourTarget,
  type InvoiceTourTargetKey,
} from '@features/virtual-tour/config/tourTargetCatalog'
import type { InvoiceTourStepAlign, InvoiceTourStepSide } from '@features/virtual-tour/types/virtualTour.types'

const POPOVER_OVERRIDES: Partial<
  Record<InvoiceTourTargetKey, Partial<NonNullable<AppTourStep['popover']>>>
> = {
  [INVOICE_TOUR_TARGET_KEY.settingsHeading]: { side: 'bottom', align: 'start' },
  [INVOICE_TOUR_TARGET_KEY.storeLogoSetting]: { side: 'left', align: 'start' },
  [INVOICE_TOUR_TARGET_KEY.settingsStoreTab]: { align: 'center' },
  [INVOICE_TOUR_TARGET_KEY.themeEditorSectionNav]: { side: 'right' },
  [INVOICE_TOUR_TARGET_KEY.themeEditorTypography]: { side: 'left', align: 'end' },
  [INVOICE_TOUR_TARGET_KEY.themeEditorEditCode]: { side: 'bottom', align: 'start' },
  [INVOICE_TOUR_TARGET_KEY.themeCodeLiquidTab]: { side: 'bottom', align: 'start' },
  [INVOICE_TOUR_TARGET_KEY.themeCodeStylesTab]: { side: 'bottom', align: 'start' },
  [INVOICE_TOUR_TARGET_KEY.themeCodePreviewButton]: { side: 'bottom', align: 'start' },
  [INVOICE_TOUR_TARGET_KEY.themeTemplateGuide]: { side: 'bottom', align: 'end' },
}

export const createInvoiceTourStep = ({
  align,
  description,
  launchContext,
  side,
  targetKey,
  title,
}: {
  align?: InvoiceTourStepAlign
  description: string
  launchContext?: Record<string, string>
  side?: InvoiceTourStepSide
  targetKey: string
  title: string
}): AppTourStep | null => {
  const target = resolveTourTarget(targetKey, launchContext)

  if (!target) {
    if (import.meta.env.DEV) {
      console.warn(`[virtual-tour] Unknown tour target key: ${targetKey}`)
    }

    return null
  }

  const popoverOverride = POPOVER_OVERRIDES[targetKey as InvoiceTourTargetKey]

  return {
    route: target.route,
    routeMatch: target.routeMatch,
    matchPaths: target.matchPaths,
    navigationRoute: target.navigationRoute,
    element: target.element,
    tooltipElement: target.tooltipElement,
    waitTimeoutMs: target.waitTimeoutMs,
    interactionMode: target.interactionMode,
    advanceOnPath: target.advanceOnPath,
    advanceOnPathMatch: target.advanceOnPathMatch,
    advanceOnClick: target.advanceOnClick,
    popover: {
      title,
      description,
      side: popoverOverride?.side ?? side,
      align: popoverOverride?.align ?? align,
      placement: popoverOverride?.placement,
    },
  }
}
